import { Injectable } from "@angular/core";
import {
  ApiAddAction,
  ApiBrowseAction,
  ApiDeleteAction,
  ApiEditAction,
  ApiReadAction
} from "../../api-actions";
import { ApiStateStoreService } from "../../api-state-store.service";
import { ApiSessionService } from "../../api-session.service";
import { sessionId } from "../../apiParams/session-id";
import { organizationId } from "../../apiParams/organization-id";
import {
  ApiMusicSource,
  MusicSource,
  apiMusicSourceToMusicSource as clean,
  MusicSourceEdit,
  MusicSourceAdd
} from "./music-source";
import { extractData, OnsipApiResponse } from "../../apiResponse/response-body-new";
import { getApiActionName } from "../../onsip-api-action-new";
import { ParameterValue } from "../../util/api-action-description";
import { ApiResourceService } from "../api-resource.service";
import { arrayToRecord } from "../../util/arrayToRecord";
import { onsipApiArrayToArray } from "../../apiResponse/xml-json";
import { ApiPromiseState, ApiPromiseStateService } from "../../api-promise-state.service";
import { map, take, withLatestFrom } from "rxjs/operators";
import { Observable } from "rxjs";

const debug = false;

@Injectable({ providedIn: "root" })
export class MusicSourceService extends ApiResourceService<MusicSource> {
  constructor(
    session: ApiSessionService,
    store: ApiStateStoreService,
    promiseState: ApiPromiseStateService
  ) {
    super(session, store, promiseState, "MusicSource", "musicSourceId");

    debug && this.state.subscribe(state => console.warn("MusicSource", state));
    this.orgState = this.currentOrgMusicSource;
  }

  // getter would be needed when we would have several PBXes
  get currentOrgMusicSource(): Observable<Array<MusicSource>> {
    return this.state.pipe(
      withLatestFrom(this.store.state.pipe(organizationId())),
      map(([substate, selfOrganizationId]) =>
        Object.values(substate.state).filter(
          ({ organizationId }) => organizationId === selfOrganizationId || organizationId === "0"
        )
      )
    );
  }

  musicSourceBrowse(
    extraParams: Record<string, ParameterValue> = {}
  ): ApiPromiseState<MusicSource> {
    this.dispatcher.next({
      parameters: {
        Action: ApiBrowseAction.MusicSourceBrowse,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        Limit: 250,
        ...extraParams
      }
    });
    return this.promiseState.toPromise(ApiBrowseAction.MusicSourceBrowse);
  }

  musicSourceRead(id: string): ApiPromiseState<MusicSource> {
    this.dispatcher.next({
      parameters: {
        Action: ApiReadAction.MusicSourceRead,
        SessionId: this.store.state.pipe(sessionId()),
        MusicSourceId: id
      }
    });
    return this.promiseState.toPromise(ApiReadAction.MusicSourceRead);
  }

  musicSourceEdit(params: MusicSourceEdit): ApiPromiseState<MusicSource> {
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.MusicSourceEdit,
        SessionId: this.store.state.pipe(sessionId()),
        MusicSourceId: params.musicSourceId,
        Name: params.name,
        Url: params.url
      }
    });
    return this.promiseState.toPromise(ApiEditAction.MusicSourceEdit);
  }

  musicSourceOrganizationDefault(musicSourceId: string): ApiPromiseState<MusicSource> {
    this.dispatcher.next({
      parameters: {
        Action: ApiEditAction.MusicSourceOrganizationDefault,
        SessionId: this.store.state.pipe(sessionId()),
        MusicSourceId: musicSourceId,
        OrganizationId: this.store.state.pipe(organizationId())
      }
    });
    return this.promiseState.toPromise(ApiEditAction.MusicSourceOrganizationDefault);
  }

  musicSourceDelete(musicSourceId: string): ApiPromiseState<MusicSource> {
    this.dispatcher.next({
      parameters: {
        Action: ApiDeleteAction.MusicSourceDelete,
        SessionId: this.store.state.pipe(sessionId()),
        MusicSourceId: musicSourceId
      }
    });
    return this.promiseState.toPromise(ApiDeleteAction.MusicSourceDelete);
  }

  musicSourceAdd(params: MusicSourceAdd): ApiPromiseState<MusicSource> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAddAction.MusicSourceAdd,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        Name: params.name,
        Url: params.url
      }
    });
    return this.promiseState.toPromise(ApiAddAction.MusicSourceAdd);
  }

  reducer(response: OnsipApiResponse): void {
    const action = getApiActionName(response);
    switch (action) {
      case ApiBrowseAction.MusicSourceBrowse:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            extractData<Array<ApiMusicSource>>(response, action, "MusicSource", "MusicSources").map(
              clean
            ),
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiEditAction.MusicSourceOrganizationDefault:
        if (!response.Context.Request.Parameters) break;
        // eslint-disable-next-line no-case-declarations
        const editedMusicSource = onsipApiArrayToArray(
          response.Context.Request.Parameters,
          "Parameter"
        ).find(param => param.Name === "MusicSourceId")?.Value;
        this.state
          .pipe(
            take(1),
            map(state => {
              for (const key of Object.keys(state.state)) {
                state.state[key]["default"] = editedMusicSource === key;
              }
              return state.state;
            })
          )
          .subscribe(musicSources => {
            this.store.mergeStateUpdate(this.resourceName, musicSources, action);
          });

        break;
      case ApiReadAction.MusicSourceRead:
      case ApiEditAction.MusicSourceEdit:
      case ApiAddAction.MusicSourceAdd:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            [clean(extractData<ApiMusicSource>(response, action, "MusicSource"))],
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiDeleteAction.MusicSourceDelete:
        if (!response.Context.Request.Parameters) break;
        // eslint-disable-next-line no-case-declarations
        const deletedMusicSource = onsipApiArrayToArray(
          response.Context.Request.Parameters,
          "Parameter"
        ).find(param => param.Name === "MusicSourceId")?.Value;
        deletedMusicSource &&
          this.store.mergeStateUpdate(
            this.resourceName,
            { [deletedMusicSource]: undefined },
            action
          );
        break;
    }
  }
}
